import type { Workout, WorkoutResult } from '../types/workout'

export interface WorkoutResultMatch {
  results: WorkoutResult[]
  completed: boolean
}

function sortWorkouts(list: Workout[]): Workout[] {
  return [...list].sort((a, b) => {
    // Unscheduled workouts are filtered out before this runs, so dates are non-null.
    const da = a.scheduled_date as string
    const db = b.scheduled_date as string
    if (da !== db) return da.localeCompare(db)
    return a.created_at.localeCompare(b.created_at)
  })
}

function sortResults(list: WorkoutResult[]): WorkoutResult[] {
  return [...list].sort((a, b) => {
    if (a.completed_on !== b.completed_on) {
      return a.completed_on.localeCompare(b.completed_on)
    }
    return a.created_at.localeCompare(b.created_at)
  })
}

/**
 * Pair scheduled workouts with workout results within the same week, per exercise.
 *
 * Algorithm (see .claude/skills/training-diary-week-results.md):
 *  - Unscheduled workouts (scheduled_date == null) never participate; they
 *    live in the unscheduled section where results aren't shown.
 *  - Group by exercise.id.
 *  - Walk workouts and results in order: workout i consumes result i.
 *  - When there are more results than workouts (m > n), the LAST workout
 *    absorbs all overflow results.
 *  - When there are fewer results than workouts (m < n), the trailing workouts
 *    remain uncompleted (no results attached).
 */
export function matchWorkoutResults(
  workouts: Workout[],
  results: WorkoutResult[]
): Map<string, WorkoutResultMatch> {
  const byWorkoutId = new Map<string, WorkoutResultMatch>()
  for (const w of workouts) {
    byWorkoutId.set(w.id, { results: [], completed: false })
  }

  const scheduled = workouts.filter((w) => w.scheduled_date != null)

  const workoutsByExercise = new Map<string, Workout[]>()
  for (const w of scheduled) {
    const key = w.exercise.id
    const list = workoutsByExercise.get(key) ?? []
    list.push(w)
    workoutsByExercise.set(key, list)
  }

  const resultsByExercise = new Map<string, WorkoutResult[]>()
  for (const r of results) {
    const key = r.exercise.id
    const list = resultsByExercise.get(key) ?? []
    list.push(r)
    resultsByExercise.set(key, list)
  }

  for (const [exerciseId, ws] of workoutsByExercise) {
    const rs = resultsByExercise.get(exerciseId) ?? []
    if (ws.length === 0 || rs.length === 0) continue

    const sortedWs = sortWorkouts(ws)
    const sortedRs = sortResults(rs)
    const n = sortedWs.length
    const m = sortedRs.length

    for (let i = 0; i < Math.min(n, m); i++) {
      const w = sortedWs[i]
      if (i === n - 1 && m > n) {
        byWorkoutId.set(w.id, { results: sortedRs.slice(i), completed: true })
      } else {
        byWorkoutId.set(w.id, { results: [sortedRs[i]], completed: true })
      }
    }
  }

  return byWorkoutId
}
